import { CheckCircle2, GitBranch, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";

export type GithubSetupVariant = "app_created" | "connected";

const TEST_STEPS = [
	"Open a project, go to its Settings tab and link a GitHub repository.",
	'Open any task and click "Create branch" in its GitHub section.',
	"Push a commit to that branch and open a pull request on GitHub.",
	"The PR badge appears on the task card and updates on merge or close.",
];

export function GithubSetupModal({
	variant,
	webhookConfigured,
	installing,
	onInstall,
	onClose,
}: {
	variant: GithubSetupVariant | null;
	webhookConfigured: boolean;
	installing: boolean;
	onInstall: () => void;
	onClose: () => void;
}) {
	return (
		<Dialog open={variant !== null} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="sm:max-w-md">
				{variant === "app_created" ? (
					<>
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2">
								<CheckCircle2 className="h-5 w-5 text-secondary" />
								GitHub App created
							</DialogTitle>
							<DialogDescription>
								Credentials were captured automatically. One step left:
								install the App on the repositories you want to use.
							</DialogDescription>
						</DialogHeader>
						<DialogFooter className="gap-2 sm:gap-0">
							<Button variant="outline" onClick={onClose}>
								Later
							</Button>
							<Button onClick={onInstall} disabled={installing}>
								{installing ? (
									<Loader2 className="h-4 w-4 animate-spin mr-2" />
								) : (
									<GitBranch className="h-4 w-4 mr-2" />
								)}
								Install on repositories
							</Button>
						</DialogFooter>
					</>
				) : (
					<>
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2">
								<CheckCircle2 className="h-5 w-5 text-secondary" />
								GitHub connected
							</DialogTitle>
							<DialogDescription>
								The App is installed. Here is how to verify everything
								works:
							</DialogDescription>
						</DialogHeader>
						<ol className="list-decimal space-y-2 pl-5 text-sm text-foreground">
							{TEST_STEPS.map((step) => (
								<li key={step}>{step}</li>
							))}
						</ol>
						{!webhookConfigured && (
							<p className="rounded-lg border border-warning/30 bg-warning/5 p-2.5 text-xs text-muted-foreground">
								No webhook is configured (typical on localhost), so PR
								status will not update live. Use the refresh icon in a
								task's GitHub section to pull the latest status on
								demand.
							</p>
						)}
						<DialogFooter>
							<Button onClick={onClose}>Got it</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}
